import React, { Fragment, useState, useEffect } from "react";
import {
  Outlet,
  useParams,
  useOutletContext,
  useNavigate,
} from "react-router-dom";
import { useReturn } from "util/return";
import { useSetTitle } from "util/title";

import { useSelector, useDispatch } from "react-redux";

import dayjs from "dayjs";
import { profileSlice } from "redux/features/profile";
import { changesSlice, changesSubslice } from "redux/features/changes";
import { RESOURCE_TYPE_EMAIL_ADDRESS, NEW_RESOURCE } from "redux/typeConstants";

import { useEmailChangesInProgress } from "hooks/emailChanges";
import { useAuthenticationState } from "redux/features/auth";

import {
  Container,
  Message,
  Notification,
  Button,
  Columns,
  Box,
  Form,
} from "react-bulma-components";

import ErrorMessage from "util/errorMessage";

import TrimmedInput from "common-frontend/util/trimmedInput";
import Loading from "util/loading";

import IconButton from "util/iconButton";
import { FaCheck, FaTimes, FaEdit, FaUserCheck, FaPlus } from "react-icons/fa";
import assessEmails from "util/assessEmails";
import { verifyEmailSlice } from "redux/features/verifyEmail";

import ResolveProfilesSharingEmail from "components/disambiguation/email";

const { Field, Label, Control, Radio, Select, Checkbox, Help } = Form;

function CloseButton() {
  const doClose = useReturn();
  return (
    <Field>
      <div className="buttons">
        <IconButton icon={FaTimes} onClick={doClose}>
          Close
        </IconButton>
      </div>
    </Field>
  );
}

function EditEmailCharacteristics({ personId, personInfo, email }) {
  const { fullName, affiliations, affiliationLookup } = personInfo;
  const haveAffiliations = affiliations.length > 0;
  const changeRecords = changesSubslice(
    useSelector(changesSlice.selector),
    personId,
    "email",
    RESOURCE_TYPE_EMAIL_ADDRESS,
    email.isNew ? NEW_RESOURCE : email.id
  );

  const saveError = changeRecords ? changeRecords.failed : null;
  const { changes } = changeRecords || { changes: {} };
  const changeKeys = {
    id: personId,
    section: "email",
    resourceType: RESOURCE_TYPE_EMAIL_ADDRESS,
    resourceId: email.isNew ? NEW_RESOURCE : email.id,
  };
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [saving, setSaving] = useState(false);

  const merging =
    Object.keys(changes).filter((k) => k.match(/merge-other-profile/)).length >
    0;

  useEffect(() => {
    if (saving && !changeRecords) {
      setSaving(false);
      if (!email.isPrimary) navigate(-1);
    }
    if (saving && changeRecords && !changeRecords.pending) {
      setSaving(false);
    }
  }, [saving, changeRecords, email.isPrimary, navigate]);

  const setChange = (field, value) => {
    if (value !== email[field])
      dispatch(
        changesSlice.actions.setChanged({
          ...changeKeys,
          field,
          value,
        })
      );
    else if (changeRecords && changeRecords.changes[field] !== undefined)
      dispatch(
        changesSlice.actions.clearChanged({
          ...changeKeys,
          field,
        })
      );
  };

  useEffect(() => {
    if (!saving) {
      if (email.isNew && !changeRecords) {
        setChange("person_id", personId);
        setChange("shared", false);
        setChange("affiliation_id", null);
      }
      setChange("valid_through", null);
      if (email.isPrimary && email.isNew) setChange("set_as_primary", true);
      if (email.noPrimary) setChange("set_as_primary", true);
    }
  });

  const doSave = () => {
    setSaving(true);
    if (changeRecords) dispatch(changesSlice.asyncActions.save(changeKeys));
  };

  const doCancel = () => {
    dispatch(changesSlice.actions.deleteChangeRecord(changeKeys));
    if (!email.isPrimary) navigate(-1);
  };

  const curValue = Object.fromEntries(
    [
      "address",
      "verification_code",
      "shared",
      "affiliation_id",
      "set_as_primary",
      "valid_through",
      "reactivate_affiliation",
      ...Object.keys(changes).filter((pseudoField) =>
        pseudoField.startsWith("merge-other-profile")
      ),
    ].map((field) => [
      field,
      changes[field] === undefined
        ? email
          ? email[field]
          : null
        : changes[field],
    ])
  );

  const curAffiliation = curValue.affiliation_id
    ? affiliationLookup[curValue.affiliation_id]
    : null;

  const otherTypeAvailable =
    email.isNew && email.isPrimary && email.others.length > 0
      ? email.others.filter((other) => other.isAlternate).length > 0
        ? "alternate"
        : "old"
      : null;

  const newAddressIsOld =
    email.isNew &&
    email.others.filter(
      (other) =>
        curValue.address &&
        other.address.toUpperCase() === curValue.address.toUpperCase()
    ).length > 0;

  const [newAddressSharedBy, setNewAddressSharedBy] = useState({});
  const sharedBy = email.isNew
    ? newAddressSharedBy[curValue.address] || []
    : email.shared_by;

  const unmergedSharedBy = sharedBy.filter(
    (other) => !curValue["merge-other-profile-" + other.id]
  );

  useEffect(() => {
    if (saving && newAddressSharedBy[curValue.address]) {
      setNewAddressSharedBy((state) => {
        const s = { ...state };
        delete s[curValue.address];
        return s;
      });
    } else if (
      saveError &&
      saveError.details.shared_by &&
      !newAddressSharedBy[curValue.address]
    )
      setNewAddressSharedBy((state) => {
        return { ...state, [curValue.address]: saveError.details.shared_by };
      });
  }, [saving, newAddressSharedBy, curValue.address, saveError]);
  const [showSharedBy, setShowSharedBy] = useState(false);

  const { authenticatedAsStaff: isStaff } = useAuthenticationState();

  const verificationApplicable = email.isNew && !newAddressIsOld;
  const verificationNeeded = verificationApplicable && !isStaff;

  const verificationStates = useSelector(verifyEmailSlice.selector)[personId];
  const verificationState =
    verificationStates && curValue.address !== null
      ? verificationStates[curValue.address] || {}
      : {};

  const sendVerificationCode = () => {
    dispatch(
      verifyEmailSlice.asyncActions.sendCode({
        id: personId,
        address: curValue.address,
      })
    );
  };
  return (
    <Box>
      <p className="box-header">
        <span className="box-label">
          {email.isPrimary
            ? "Preferred contact address"
            : email.isNew
            ? "Adding e-mail address"
            : "Editing alternate address"}
        </span>
        {email.isNew ? null : " "}
        {email.isNew ? null : (
          <span className="box-value">{email.address}</span>
        )}
      </p>

      {otherTypeAvailable ? (
        <p>
          No preferred e-mail address has been set. If one of your{" "}
          {otherTypeAvailable} addresses below should be used, please{" "}
          {otherTypeAvailable === "old"
            ? "reactivate it"
            : "set it as preferred"}{" "}
          (below). Otherwise please enter your preferred e-mail address here:
        </p>
      ) : null}
      {email.isNew ? (
        <Field>
          <Label>Address</Label>
          <Control>
            <TrimmedInput
              type="email"
              value={curValue.address ?? ""}
              setValue={(v) => setChange("address", v === "" ? null : v)}
            />
          </Control>
        </Field>
      ) : null}
      {newAddressIsOld ? (
        <Field>
          <p className="has-text-danger">
            This address is already attached to your profile. Changes you make
            here will merely update its characteristics, not add a new e-mail
            address.
          </p>
        </Field>
      ) : null}
      <Columns className="prompt-shared-email">
        <Columns.Column size={12}>
          <Field>
            <Label>Shared or not shared?</Label>
            <Control>
              <Radio
                name="edit-email-shared"
                checked={!curValue.shared}
                onChange={(e) => {
                  setChange("shared", false);
                }}
              >
                {" "}
                <b>Not shared:</b> Belongs only to you ({fullName}) and you
                don't use it to register on behalf of other people.
              </Radio>
            </Control>
            <Control>
              <Radio
                name="edit-email-shared"
                checked={!!curValue.shared}
                onChange={(e) => {
                  setChange("shared", true);
                }}
              >
                {" "}
                <b>Shared:</b> Used by others (e.g. family or
                secretary/assistant), or used to register others, or belongs to
                a role (like "chair@...").
              </Radio>
            </Control>
            <Help>
              This setting helps us match registrations to the correct profile.
            </Help>
          </Field>
          {sharedBy.length === 0 ? null : (
            <Fragment>
              {curValue.shared || unmergedSharedBy.length === 0 ? (
                <p>
                  This e-mail is also attached to
                  {sharedBy.length > 1
                    ? ` ${sharedBy.length} other people's profiles`
                    : " another person's profile"}
                  .
                  {curValue.shared &&
                  unmergedSharedBy.length === sharedBy.length ? (
                    <Button
                      className="is-ghost"
                      onClick={(e) => {
                        setShowSharedBy((show) => !show);
                        e.target.blur();
                      }}
                    >
                      {showSharedBy ? "Hide " : "Show "}
                      the other profile
                      {sharedBy.length > 1 ? "s" : ""}
                    </Button>
                  ) : null}
                </p>
              ) : (
                <ErrorMessage>
                  This address cannot be marked "Not shared" because it is
                  currently linked to{" "}
                  {sharedBy.length > 1
                    ? "other people's profiles."
                    : "another person's profile."}{" "}
                  Please review and correct the{" "}
                  {sharedBy.length > 1 ? "profiles" : "profile"} below.
                </ErrorMessage>
              )}
              {showSharedBy ||
              !curValue.shared ||
              unmergedSharedBy.length < sharedBy.length ? (
                <Notification color="warning" light textColor="black">
                  <ResolveProfilesSharingEmail
                    changeKeys={changeKeys}
                    address={curValue.address}
                    sharedBy={sharedBy}
                    editState={curValue}
                  />

                  {curValue.shared ? (
                    <Button remove onClick={(e) => setShowSharedBy(false)} />
                  ) : null}
                </Notification>
              ) : null}
            </Fragment>
          )}
        </Columns.Column>
      </Columns>

      <Columns className="prompt-email-affiliation">
        <Columns.Column sizr={12}>
          <Field>
            <Label>Linked to a particular institution?</Label>
            {haveAffiliations ? (
              <Fragment>
                <Control>
                  <Select
                    className="is-fullwidth"
                    onChange={(e) => {
                      setChange(
                        "affiliation_id",
                        e.target.value === "" ? null : Number(e.target.value)
                      );
                      setChange("reactivate_affiliation", undefined);
                    }}
                    value={curValue.affiliation_id ?? ""}
                  >
                    <option value="">
                      No - it will remain valid even if my affiliations change
                    </option>
                    {affiliations.map((affil) => (
                      <option
                        key={affil.affiliation_id}
                        value={affil.affiliation_id}
                      >
                        Yes - it's my e-mail while I am at{" "}
                        {affil.institution_name}
                      </option>
                    ))}
                  </Select>
                </Control>
                <Help>
                  This setting tells us whether or not to ask you for an updated
                  e-mail address if your affiliation changes.
                </Help>
              </Fragment>
            ) : (
              <p>
                If you later add an affiliation to your profile, there will be
                an option here to specify whether or not the e-mail is
                affiliation related and subject to change if your affiliation
                changes.
              </p>
            )}
          </Field>
        </Columns.Column>
      </Columns>
      {curAffiliation && curAffiliation.last_valid_on ? (
        <Fragment>
          <ErrorMessage>
            According to our records, you are no longer affiliated with{" "}
            {curAffiliation.institution_name}. If this e-mail address is still
            current, please either unlink it from this affiliation or select the
            option below for reactivating the affiliation:
          </ErrorMessage>
          <Field>
            <Control>
              <Checkbox
                checked={!!curValue.reactivate_affiliation}
                onChange={(e) =>
                  setChange(
                    "reactivate_affiliation",
                    e.target.checked ? true : undefined
                  )
                }
              >
                {" "}
                Reactivate my affiliation with {curAffiliation.institution_name}
              </Checkbox>
            </Control>
          </Field>
        </Fragment>
      ) : null}

      {email.isPrimary ? null : (
        <Field>
          {email.noPrimary ? (
            <p>
              Since you have no preferred contact e-mail set yet, this will
              automatically be set as your preferred contact e-mail once you
              save changes.
            </p>
          ) : (
            <Control>
              <Checkbox
                checked={!!curValue.set_as_primary}
                value="on"
                onChange={(e) =>
                  setChange(
                    "set_as_primary",
                    e.target.checked ? true : undefined
                  )
                }
              >
                {" "}
                Make this your preferred contact address
              </Checkbox>
            </Control>
          )}
        </Field>
      )}

      {verificationApplicable ? (
        <Field>
          <Label>Verification Code</Label>
          {isStaff ? (
            <p>
              When someone updates their own information we require they enter a
              verification code, that gets sent to their e-mail address. This
              ensures the address is accurate and belongs to them. We can't do
              this when staff members are updating someone else's address,
              though, so be sure you have entered the address correctly. No
              verification code will be sent.
            </p>
          ) : (
            <Fragment>
              {verificationState.failed ? (
                <ErrorMessage header="Failed to send verification code">
                  {verificationState.failed.message}
                </ErrorMessage>
              ) : null}
              <p>
                To verify your e-mail address, we
                {verificationState.sending
                  ? " are sending "
                  : verificationState.sent
                  ? " have sent "
                  : " will send "}
                a verification code to that address. Please enter the code here
                when you receive it.
              </p>
              <Control>
                <TrimmedInput
                  type="text"
                  value={curValue.verification_code ?? ""}
                  setValue={(v) =>
                    setChange("verification_code", v === "" ? null : v)
                  }
                />
              </Control>
            </Fragment>
          )}
        </Field>
      ) : null}

      <Field>
        {saveError ? (
          <ErrorMessage
            header={
              email.isNew && !newAddressIsOld
                ? "Unable to Add E-mail Address"
                : "Unable to Save Changes"
            }
          >
            {saveError.message}
          </ErrorMessage>
        ) : null}
        {saving && !saveError ? (
          <Message color="warning" className="saving">
            <Message.Body>
              Saving; please wait
              {merging ? (
                <b>
                  <i>
                    {" "}
                    It could take several minutes to merge all the registration
                    records into the one profile.
                  </i>
                </b>
              ) : null}
            </Message.Body>
          </Message>
        ) : changeRecords || !email.isPrimary ? (
          <div className="buttons">
            {verificationNeeded ? (
              <IconButton icon={FaUserCheck} onClick={sendVerificationCode}>
                {verificationState.sending || verificationState.sent
                  ? "Re-Send"
                  : "Send"}{" "}
                Verification Code
              </IconButton>
            ) : null}

            {changeRecords ? (
              <IconButton
                icon={FaCheck}
                onClick={doSave}
                disabled={verificationNeeded && !curValue.verification_code}
              >
                {email.isNew && !newAddressIsOld
                  ? verificationNeeded && !curValue.verification_code
                    ? "Confirm Address (after entering verification code)"
                    : "Confirm Address and Add to Profile"
                  : "Save Changes"}
              </IconButton>
            ) : null}

            <IconButton icon={FaTimes} onClick={doCancel}>
              {changeRecords ? "Cancel Changes" : "Close"}
            </IconButton>
          </div>
        ) : null}
      </Field>
    </Box>
  );
}

function ReviewAltEmail({ personId, email }) {
  const changeRecords = changesSubslice(
    useSelector(changesSlice.selector),
    personId,
    "email",
    RESOURCE_TYPE_EMAIL_ADDRESS,
    email.id
  );
  const changeKeys = {
    id: personId,
    section: "email",
    resourceType: RESOURCE_TYPE_EMAIL_ADDRESS,
    resourceId: email.id,
  };

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const actionPending = changeRecords ? changeRecords.pending : null;

  const [failMessage, setFailMessage] = useState(null);

  useEffect(() => {
    if (changeRecords && changeRecords.failed) {
      setFailMessage(
        "Failed to " +
          (changeRecords.changes.set_as_primary
            ? "set " + email.address + " as primary"
            : changeRecords.changes.valid_through
            ? "deactivate " + email.address
            : "save changes to " + email.address) +
          ": " +
          changeRecords.failed.message
      );
    }
  }, [changeRecords, email.address]);

  useEffect(() => {
    if (changeRecords && !changeRecords.pending) {
      dispatch(
        changesSlice.actions.clearChanged({
          ...changeKeys,
          field: "set_as_primary",
        })
      );
      dispatch(
        changesSlice.actions.clearChanged({
          ...changeKeys,
          field: "valid_through",
        })
      );
    }
  });

  const doEdit = () => navigate("edit/" + email.id);

  const doSetPrimary = () => {
    dispatch(
      changesSlice.actions.setChanged({
        ...changeKeys,
        field: "set_as_primary",
        value: true,
      })
    );
    dispatch(changesSlice.asyncActions.save(changeKeys));
  };

  const doDeactivate = () => {
    dispatch(
      changesSlice.actions.setChanged({
        ...changeKeys,
        field: "valid_through",
        value: dayjs().subtract(1, "day").format("YYYY-MM-DD"),
      })
    );
    dispatch(changesSlice.asyncActions.save(changeKeys));
  };

  return (
    <Fragment>
      {failMessage ? (
        <Columns.Column size={12}>
          <ErrorMessage>{failMessage}</ErrorMessage>
        </Columns.Column>
      ) : null}
      <Columns.Column size={4} className="email">
        {email.address}
      </Columns.Column>

      <Columns.Column size={4}>
        {actionPending ? (
          <span className="loading-text">
            {actionPending.set_as_primary
              ? "Setting as preferred ..."
              : actionPending.valid_through
              ? "Removing ..."
              : "Saving ..."}
            <Loading />
          </span>
        ) : (
          <div className="buttons">
            <IconButton icon={FaCheck} onClick={doSetPrimary}>
              Set as Preferred
            </IconButton>

            <IconButton icon={FaTimes} onClick={doDeactivate}>
              Remove (Deactivate)
            </IconButton>
          </div>
        )}
      </Columns.Column>
      <Columns.Column size={4}>
        {email.shared ? "Shared. " : "Not shared. "}
        {email.institution
          ? "Linked to " + email.institution + "."
          : "Not linked to an institution."}
        {changeRecords ? (
          <div className="unsaved-changed-message">
            You have unsaved changes
          </div>
        ) : null}
        <div className="buttons">
          {actionPending ? null : (
            <IconButton icon={FaEdit} onClick={doEdit}>
              {changeRecords ? "Resume Editing" : "Edit these characteristics"}
            </IconButton>
          )}
        </div>
      </Columns.Column>
    </Fragment>
  );
}

function ReviewOldEmail({ personId, email }) {
  const navigate = useNavigate();

  const changeRecords = changesSubslice(
    useSelector(changesSlice.selector),
    personId,
    "email",
    RESOURCE_TYPE_EMAIL_ADDRESS,
    email.id
  );

  const doEdit = () => navigate("edit/" + email.id);

  return (
    <Fragment>
      <Columns.Column size={4} className="email">
        {email.address}
      </Columns.Column>

      <Columns.Column size={4}>
        <div className="buttons">
          {changeRecords ? (
            <div className="unsaved-changed-message">
              Reactivation was begun but not saved.
            </div>
          ) : null}

          <IconButton icon={FaCheck} onClick={doEdit}>
            {changeRecords ? "Resume reactivation" : "Reactivate"}
          </IconButton>
        </div>
      </Columns.Column>
      <Columns.Column size={4}>
        {email.shared ? "Shared" : "Not shared"},{" "}
        {email.institution
          ? "linked to " + email.institution
          : "not linked to an institution"}
        <div className="buttons">
          <IconButton icon={FaEdit} onClick={doEdit}>
            Reactivate to edit these characteristics
          </IconButton>
        </div>
      </Columns.Column>
    </Fragment>
  );
}

export function ReviewAddresses() {
  const { personId, profile } = useOutletContext();

  const {
    emails,
    primaryEmail,
    alternateEmails,
    oldEmails,
    personInfo,
  } = assessEmails(profile);

  const navigate = useNavigate();

  const doAdd = () => navigate("add");

  const {
    additionalAddressBeingAdded,
    cancelUnfinishedAddition,
  } = useEmailChangesInProgress(personId, !primaryEmail);

  return (
    <Fragment>
      {additionalAddressBeingAdded ? (
        <Box>
          Unfinished addition of new address {additionalAddressBeingAdded}.
          <Field>
            <div className="buttons">
              <IconButton icon={FaEdit} onClick={doAdd}>
                Resume
              </IconButton>
              <IconButton icon={FaTimes} onClick={cancelUnfinishedAddition}>
                Cancel
              </IconButton>
            </div>
          </Field>
        </Box>
      ) : null}

      <EditEmailCharacteristics
        personId={personId}
        personInfo={personInfo}
        email={
          primaryEmail || {
            isNew: true,
            isPrimary: true,
            others: emails,
          }
        }
        isPrimary
      />
      {primaryEmail ? (
        <Field>
          <div className="buttons">
            <IconButton icon={FaPlus} onClick={doAdd}>
              Add (or Switch to) New E-mail Address
            </IconButton>
          </div>
        </Field>
      ) : null}
      {alternateEmails.length > 0 ? (
        <Box>
          <p className="box-header">
            <span className="box-label">Alternate E-mail Addresses</span>
          </p>
          <Columns className="is-vcentered review-alt-email">
            {alternateEmails.map((email) => (
              <ReviewAltEmail key={email.id} {...{ personId, email }} />
            ))}
          </Columns>
        </Box>
      ) : null}
      {oldEmails.length > 0 ? (
        <Box>
          <p className="box-header">
            <span className="box-label">Old E-mail Addresses</span>
          </p>
          <p>
            These addresses were on past registrations but are marked as being
            no longer valid.
          </p>
          <Columns className="is-vcentered review-alt-email">
            {oldEmails.map((email) => (
              <ReviewOldEmail key={email.id} {...{ personId, email }} />
            ))}
          </Columns>
        </Box>
      ) : null}
      <CloseButton />
    </Fragment>
  );
}

export function EditAltEmail() {
  const { personId, profile } = useOutletContext();
  const { emailId: emailIdString } = useParams();
  const emailId = Number(emailIdString);
  const { emails, personInfo } = assessEmails(profile);
  const [email] = emails.filter((email) => email.id === emailId);
  return email ? (
    <EditEmailCharacteristics
      personId={personId}
      personInfo={personInfo}
      email={email}
    />
  ) : (
    <ErrorMessage>
      Invalid e-mail id {emailId}
      <CloseButton />
    </ErrorMessage>
  );
}

export function AddAltEmail() {
  const { personId, profile } = useOutletContext();
  const { emails, personInfo } = assessEmails(profile);
  const email = {
    isNew: true,
    others: emails,
  };
  return (
    <EditEmailCharacteristics
      personId={personId}
      personInfo={personInfo}
      email={email}
    />
  );
}
export function EmailEditorLayout() {
  const { id: personId, data: profile } = useSelector(profileSlice.selector);
  const doClose = useReturn();
  useSetTitle("Email Addresses");
  return (
    <Container className="spaced">
      <Message>
        <Message.Header>
          E-mail Addresses <Button remove onClick={doClose} />
        </Message.Header>
        <Message.Body className="basic-edit-emails">
          <Outlet context={{ personId, profile }} />
        </Message.Body>
      </Message>
    </Container>
  );
}
